import { Button } from "@/components/ui/button";
import { ErrorBoundary } from "@/shared/components/error-boundary";
import { cn } from "@/shared/lib/utils";
import { useLocation } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { HcaPublicFooter } from "./hca-public-footer";
import { HcaPublicHeader } from "./hca-public-header";
import { PublicActionsProvider } from "./public-actions";

interface PublicShellProps {
  children: ReactNode;
  diagnozaContent?: ReactNode;
  kontaktContent?: ReactNode;
}

/** Veřejný layout — header + main + footer, bez AppShell sidebar. */
export function PublicShell({ children, diagnozaContent, kontaktContent }: PublicShellProps) {
  const location = useLocation();
  const isHome = location.pathname === "/";

  return (
    <PublicActionsProvider diagnozaContent={diagnozaContent} kontaktContent={kontaktContent}>
      <div className="flex min-h-screen flex-col bg-background" data-testid="hca-public-shell">
        <HcaPublicHeader />
        <main
          id="main-content"
          className={cn("flex-1", !isHome && "pt-16")}
          data-testid="hca-public-main"
        >
          <ErrorBoundary
            fallback={(error, reset) => (
              <div
                className="mx-auto max-w-xl px-4 py-24 text-center"
                data-testid="hca-public-error"
              >
                <h2 className="font-bold text-2xl text-foreground">Něco se pokazilo</h2>
                <p className="mt-3 text-muted-foreground">
                  Tuto sekci se nepodařilo načíst. Zkuste to prosím znovu.
                </p>
                <div
                  className="mx-auto mt-4 max-w-md rounded-lg bg-destructive/10 p-3 text-destructive text-sm"
                  role="alert"
                >
                  {error.message}
                </div>
                <Button onClick={reset} className="mt-6" data-testid="hca-public-error-retry">
                  Zkusit znovu
                </Button>
              </div>
            )}
          >
            {children}
          </ErrorBoundary>
        </main>
        <HcaPublicFooter />
      </div>
    </PublicActionsProvider>
  );
}
